import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../../../context/auth-context";

/** =========================================================
 * Tipos
 * ======================================================= */

export type ColaboradorAPI = {
  id?: string | number;
  nome_completo: string;
  cpf?: string | null;
  setor?: string | null;
  data_de_nascimento?: string | null;
  status_atual?: string | null;
};

type RespAPI = {
  ok?: boolean;
  meta?: any;
  data?: ColaboradorAPI[] | any[];
  erro?: string;
  detalhe?: string;
  message?: string;

  changed?: boolean;
  versao?: number | string | null;
};

type FetchColaboradoresParams = {
  empresaId: number;
  accessToken: string | null;
  signal?: AbortSignal;
};

type AwaitResult = {
  changed: boolean;
  versao: number | null;
};

/** =========================================================
 * Configuração
 * ======================================================= */

const API_BASE = "https://api.eva-googlemarine.com";

const COLABORADORES_API_URL = `${API_BASE}/departamentos/recursos-humanos/colaboradores-completo`;

const COLABORADORES_AWAIT_API_URL = `${API_BASE}/departamentos/recursos-humanos/colaboradores/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** =========================================================
 * Utilitários
 * ======================================================= */

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const jitter = (ms: number): number => ms + Math.floor(Math.random() * 400);

function toPositiveInt(value: unknown): number | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = Number(value);

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
}

/**
 * O AuthContext trabalha com mais de uma chave de token.
 * Mantemos as mesmas possibilidades aqui.
 */
function getAccessToken(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const keys = ["accessToken", "eva:accessToken", "access_token"];

  try {
    for (const key of keys) {
      const value = window.localStorage.getItem(key);

      if (value && value.trim()) {
        return value.trim();
      }
    }
  } catch {
    return null;
  }

  return null;
}

/**
 * Prioriza a empresa ativa selecionada no sistema.
 */
function resolveEmpresaId(sessao: any): number | null {
  return (
    toPositiveInt(sessao?.empresa_ativa_id) ??
    toPositiveInt(sessao?.empresa_id) ??
    toPositiveInt(sessao?.empresa?.id) ??
    toPositiveInt(sessao?.empresa_principal_id) ??
    null
  );
}

function buildRequestHeaders(
  accessToken: string | null
): Record<string, string> {
  return {
    Accept: "application/json",

    ...(accessToken
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : {}),
  };
}

async function safeJson(response: Response): Promise<RespAPI | null> {
  const text = await response.text().catch(() => "");

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as RespAPI;
  } catch {
    return {
      ok: false,
      erro: text,
    };
  }
}

/**
 * Aceita variações de nomenclatura vindas do backend.
 */
function normalizeColaboradores(arr: any[]): ColaboradorAPI[] {
  return (Array.isArray(arr) ? arr : []).map((item) => ({
    id: item?.id ?? item?.colaborador_id ?? item?.user_id ?? undefined,

    nome_completo:
      item?.nome_completo ??
      item?.nomeCompleto ??
      item?.nome ??
      item?.full_name ??
      "",

    cpf: item?.cpf ?? item?.CPF ?? item?.documento ?? null,

    setor: item?.setor ?? item?.departamento ?? item?.area ?? null,

    data_de_nascimento:
      item?.data_de_nascimento ?? item?.nascimento ?? item?.birth_date ?? null,

    status_atual:
      item?.status_atual ??
      item?.statusAtual ??
      item?.status ??
      item?.situacao ??
      null,
  }));
}

/** =========================================================
 * Requisição principal
 * ======================================================= */

export async function fetchColaboradoresOnce({
  empresaId,
  accessToken,
  signal,
}: FetchColaboradoresParams): Promise<{
  data: ColaboradorAPI[];
  meta: RespAPI["meta"] | null;
}> {
  /*
   * A constante não se chama mais URL.
   * Assim, o construtor global URL continua disponível.
   */
  const requestUrl = new URL(COLABORADORES_API_URL);

  /*
   * Enviamos a empresa ativa também como query parameter.
   * O backend deve preferir o token, mas isso mantém
   * compatibilidade com endpoints que ainda leem empresa_id.
   */
  requestUrl.searchParams.set("empresa_id", String(empresaId));

  requestUrl.searchParams.set("empresa_ativa_id", String(empresaId));

  const response = await fetch(requestUrl.toString(), {
    method: "GET",

    headers: buildRequestHeaders(accessToken),

    /*
     * Permite autenticação por cookie, caso exista.
     */
    credentials: "include",

    cache: "no-store",
    signal,
  });

  const json = await safeJson(response);

  if (!response.ok) {
    throw new Error(
      json?.erro ||
        json?.detalhe ||
        json?.message ||
        `Falha ao buscar colaboradores. HTTP ${response.status}`
    );
  }

  if (json?.ok === false) {
    throw new Error(
      json?.erro ||
        json?.detalhe ||
        json?.message ||
        "A API recusou a consulta de colaboradores"
    );
  }

  /*
   * Aceita:
   *
   * {
   *   ok: true,
   *   data: [...]
   * }
   *
   * e também array direto, caso o backend seja alterado.
   */
  const rawData = Array.isArray(json)
    ? json
    : Array.isArray(json?.data)
    ? json!.data
    : [];

  return {
    data: normalizeColaboradores(rawData),
    meta: json?.meta ?? null,
  };
}

/** =========================================================
 * Long polling
 * ======================================================= */

async function awaitChange(
  since: number,
  empresaId: number,
  accessToken: string | null,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const requestUrl = new URL(COLABORADORES_AWAIT_API_URL);

    requestUrl.searchParams.set("since", String(since));

    requestUrl.searchParams.set("timeout_ms", String(AWAIT_TIMEOUT_MS));

    requestUrl.searchParams.set("empresa_id", String(empresaId));

    requestUrl.searchParams.set("empresa_ativa_id", String(empresaId));

    const response = await fetch(requestUrl.toString(), {
      method: "GET",
      headers: buildRequestHeaders(accessToken),
      credentials: "include",
      cache: "no-store",
      signal,
    });

    /*
     * Se o endpoint /await não existir ou não estiver
     * autorizado, o hook utiliza polling convencional.
     */
    if (!response.ok) {
      return null;
    }

    const json = await safeJson(response);

    if (!json || json.ok === false) {
      return null;
    }

    const parsedVersion =
      json.versao !== undefined && json.versao !== null
        ? Number(json.versao)
        : since;

    return {
      changed: Boolean(json.changed),
      versao: Number.isFinite(parsedVersion) ? parsedVersion : since,
    };
  } catch (error: any) {
    if (error?.name === "AbortError") {
      return null;
    }

    return null;
  }
}

/** =========================================================
 * Hook
 * ======================================================= */

/**
 * O nome useEmpresasCliente é mantido para não quebrar
 * os imports já existentes no sistema.
 */
export function useEmpresasCliente() {
  const { sessao, loading: authLoading, refresh: refreshAuth } = useAuth();

  const empresaId = useMemo(
    () => resolveEmpresaId(sessao),
    [
      sessao?.empresa_ativa_id,
      sessao?.empresa_id,
      sessao?.empresa?.id,
      sessao?.empresa_principal_id,
    ]
  );

  const [data, setData] = useState<ColaboradorAPI[]>([]);

  const [loading, setLoading] = useState(true);

  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const [error, setError] = useState<string | null>(null);

  const stopRef = useRef(false);
  const fetchingRef = useRef(false);

  const empresaIdRef = useRef<number | null>(empresaId);

  const versionRef = useRef<number | null>(null);

  const awaitSupportedRef = useRef<boolean | null>(null);

  const awaitInFlightRef = useRef(false);

  const awaitControllerRef = useRef<AbortController | null>(null);

  const loadControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    empresaIdRef.current = empresaId;
  }, [empresaId]);

  /**
   * Carrega os colaboradores da empresa informada.
   */
  const carregar = useCallback(
    async (
      currentEmpresaId: number,
      signal?: AbortSignal
    ): Promise<boolean> => {
      if (fetchingRef.current) {
        return false;
      }

      fetchingRef.current = true;

      try {
        const accessToken = getAccessToken();

        const result = await fetchColaboradoresOnce({
          empresaId: currentEmpresaId,
          accessToken,
          signal,
        });

        /*
         * Impede resposta antiga de uma empresa anterior
         * sobrescrever os dados da empresa atual.
         */
        if (
          stopRef.current ||
          signal?.aborted ||
          empresaIdRef.current !== currentEmpresaId
        ) {
          return false;
        }

        setData(result.data);
        setMeta(result.meta);
        setError(null);

        return true;
      } catch (requestError: any) {
        if (
          requestError?.name === "AbortError" ||
          signal?.aborted ||
          stopRef.current
        ) {
          return false;
        }

        const message =
          requestError?.message || "Falha ao carregar colaboradores";

        console.error("Erro ao buscar colaboradores:", requestError);

        setData([]);
        setMeta(null);
        setError(message);

        return false;
      } finally {
        fetchingRef.current = false;
      }
    },
    []
  );

  useEffect(() => {
    stopRef.current = false;

    versionRef.current = null;
    awaitSupportedRef.current = null;
    awaitInFlightRef.current = false;

    awaitControllerRef.current?.abort();
    loadControllerRef.current?.abort();

    awaitControllerRef.current = null;
    loadControllerRef.current = null;

    setData([]);
    setMeta(null);
    setError(null);

    /*
     * Aguarda o AuthContext terminar a carga.
     */
    if (authLoading) {
      setLoading(true);

      return () => {
        stopRef.current = true;
        awaitControllerRef.current?.abort();
        loadControllerRef.current?.abort();
      };
    }

    /*
     * O contexto foi carregado, mas não possui empresa.
     */
    if (!empresaId) {
      setLoading(false);

      setError("Não foi possível identificar a empresa ativa na sessão.");

      return () => {
        stopRef.current = true;
        awaitControllerRef.current?.abort();
        loadControllerRef.current?.abort();
      };
    }

    empresaIdRef.current = empresaId;

    const executar = async () => {
      try {
        setLoading(true);

        /*
         * Caso a sessão esteja carregada, mas o token ainda
         * não esteja no storage, atualiza o AuthContext.
         */
        if (!getAccessToken()) {
          await refreshAuth().catch(() => undefined);
        }

        const firstController = new AbortController();

        loadControllerRef.current = firstController;

        await carregar(empresaId, firstController.signal);

        if (stopRef.current || empresaIdRef.current !== empresaId) {
          return;
        }

        setLoading(false);

        /*
         * Detecta se o long polling é suportado.
         */
        const initialAwait = await awaitChange(-1, empresaId, getAccessToken());

        if (initialAwait && typeof initialAwait.versao === "number") {
          versionRef.current = initialAwait.versao;

          awaitSupportedRef.current = true;
        } else {
          awaitSupportedRef.current = false;
        }

        /*
         * Loop de atualização.
         */
        while (!stopRef.current && empresaIdRef.current === empresaId) {
          while (fetchingRef.current && !stopRef.current) {
            await sleep(25);
          }

          if (stopRef.current || empresaIdRef.current !== empresaId) {
            break;
          }

          const currentVersion = versionRef.current;

          /*
           * Fluxo por long polling.
           */
          if (awaitSupportedRef.current !== false && currentVersion !== null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const awaitController = new AbortController();

            awaitControllerRef.current = awaitController;

            awaitInFlightRef.current = true;

            let result: AwaitResult | null = null;

            try {
              result = await awaitChange(
                currentVersion,
                empresaId,
                getAccessToken(),
                awaitController.signal
              );
            } finally {
              if (awaitControllerRef.current === awaitController) {
                awaitControllerRef.current = null;
              }

              awaitInFlightRef.current = false;
            }

            if (stopRef.current || empresaIdRef.current !== empresaId) {
              break;
            }

            /*
             * Endpoint /await indisponível:
             * passa para polling convencional.
             */
            if (result === null) {
              awaitSupportedRef.current = false;

              await sleep(jitter(ERROR_BACKOFF_MS));

              continue;
            }

            const versionAdvanced = (result.versao ?? -1) > currentVersion;

            if (result.changed || versionAdvanced) {
              versionRef.current = result.versao ?? currentVersion;

              const loadController = new AbortController();

              loadControllerRef.current = loadController;

              await carregar(empresaId, loadController.signal);

              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }

            continue;
          }

          /*
           * Polling convencional.
           */
          await sleep(jitter(POLL_FALLBACK_MS));

          if (stopRef.current || empresaIdRef.current !== empresaId) {
            break;
          }

          const pollingController = new AbortController();

          loadControllerRef.current = pollingController;

          await carregar(empresaId, pollingController.signal);
        }
      } catch (loopError: any) {
        if (!stopRef.current) {
          console.error("useEmpresasCliente loop error:", loopError);

          setError(
            loopError?.message || "Falha no monitoramento dos colaboradores"
          );
        }
      } finally {
        if (!stopRef.current) {
          setLoading(false);
        }
      }
    };

    void executar();

    return () => {
      stopRef.current = true;

      awaitControllerRef.current?.abort();
      loadControllerRef.current?.abort();

      awaitControllerRef.current = null;
      loadControllerRef.current = null;
    };
  }, [authLoading, empresaId, carregar, refreshAuth]);

  /**
   * Atualização manual.
   */
  const refresh = useCallback(async () => {
    if (authLoading) {
      return;
    }

    const currentEmpresaId = empresaIdRef.current;

    if (!currentEmpresaId) {
      setError("Empresa ativa não identificada na sessão.");

      return;
    }

    while (fetchingRef.current) {
      await sleep(25);
    }

    loadControllerRef.current?.abort();

    const controller = new AbortController();

    loadControllerRef.current = controller;

    setLoading(true);

    try {
      await carregar(currentEmpresaId, controller.signal);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [authLoading, carregar]);

  return {
    data,
    loading: loading || authLoading,
    refresh,
    meta,
    error,
    empresaId,
  };
}
